import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/plans");
    setPlans(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (p) => {
    try {
      const payload = { ...p, features: typeof p.features === "string" ? p.features.split("\n").map(s => s.trim()).filter(Boolean) : p.features };
      await api.post("/admin/plans", payload);
      toast.success("Plan saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <>
      <PageHeader eyebrow="Catalog" title="Plans & pricing" description="All prices and features are configurable here." testid="admin-plans-header" />
      <div className="px-4 sm:px-6 md:px-10 py-8 grid lg:grid-cols-2 gap-3">
        {plans.map((p) => {
          const isEditing = editing?.slug === p.slug;
          const cur = isEditing ? editing : p;
          return (
            <div key={p.slug} className="border border-border p-5" data-testid={`admin-plan-${p.slug}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label-eyebrow">{cur.slug}</div>
                  <h3 className="font-bold text-xl" style={{ fontFamily: "var(--font-heading)" }}>{cur.name}</h3>
                </div>
                <Switch checked={!!cur.is_active} onCheckedChange={(v) => setEditing({ ...cur, is_active: v })} data-testid={`admin-plan-active-${p.slug}`} />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <FieldEdit label="Monthly INR" type="number" value={cur.monthly_price_inr} onChange={(v) => setEditing({ ...cur, monthly_price_inr: Number(v) })} testid={`plan-${p.slug}-monthly-inr`} />
                <FieldEdit label="Yearly INR" type="number" value={cur.yearly_price_inr} onChange={(v) => setEditing({ ...cur, yearly_price_inr: Number(v) })} testid={`plan-${p.slug}-yearly-inr`} />
                <FieldEdit label="Monthly USD" type="number" value={cur.monthly_price_usd} onChange={(v) => setEditing({ ...cur, monthly_price_usd: Number(v) })} testid={`plan-${p.slug}-monthly-usd`} />
                <FieldEdit label="Yearly USD" type="number" value={cur.yearly_price_usd} onChange={(v) => setEditing({ ...cur, yearly_price_usd: Number(v) })} testid={`plan-${p.slug}-yearly-usd`} />
                <FieldEdit label="Seat limit" type="number" value={cur.seat_limit} onChange={(v) => setEditing({ ...cur, seat_limit: Number(v) })} testid={`plan-${p.slug}-seats`} />
                <FieldEdit label="Storage GB" type="number" value={cur.storage_gb} onChange={(v) => setEditing({ ...cur, storage_gb: Number(v) })} testid={`plan-${p.slug}-storage`} />
              </div>
              <Label className="label-eyebrow mb-1 inline-block">Features (one per line)</Label>
              <Textarea rows={5} className="text-xs mb-3" value={Array.isArray(cur.features) ? cur.features.join("\n") : cur.features} onChange={(e) => setEditing({ ...cur, features: e.target.value })} data-testid={`plan-${p.slug}-features`} />
              <div className="flex gap-2 justify-end">
                {!isEditing ? (
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...p })} className="rounded-none" data-testid={`plan-${p.slug}-edit`}>Edit</Button>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="rounded-none">Cancel</Button>
                    <Button size="sm" onClick={() => save(editing)} className="rounded-none" data-testid={`plan-${p.slug}-save`}>Save</Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function FieldEdit({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1 inline-block">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs" data-testid={testid} />
    </div>
  );
}
